import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { confService, authService } from '../../../Services';
import { apiUrl } from '../../../Services/config';

import Loader from '../../Common/Loader';
import {ReactComponent as Loading} from '../../../images/icons/General/loading-01.svg';

// ── Custom blot: <hr> ────────────────────────────────────────────────────────
const BlockEmbed = Quill.import('blots/block/embed');
class HrBlot extends BlockEmbed {
    static create() { return document.createElement('hr'); }
    static value() { return true; }
}
HrBlot.blotName = 'hr';
HrBlot.tagName  = 'hr';
Quill.register(HrBlot);

// ── Toolbar icons ─────────────────────────────────────────────────────────────
const icons = Quill.import('ui/icons');
icons['file'] = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
</svg>`;
icons['hr'] = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
  <line x1="3" y1="12" x2="21" y2="12"/>
</svg>`;

export const PageEditForm = () => {
    const params = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [fileUploading, setFileUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const quillRef = useRef(null);
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);

    const changeValue = (key, value) => {
        setData(prev => ({ ...prev, [key]: value }));
    };

    const uploadAndInsert = useCallback(async (file, type) => {
        setFileUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await authService.handleResponse(
                apiUrl + '/configuration/pages/upload',
                { method: 'POST', body: formData }
            );

            if (res?.url) {
                const quill = quillRef.current?.getEditor();
                if (quill) {
                    const range = quill.getSelection(true);
                    const index = range ? range.index : quill.getLength();

                    if (type === 'image') {
                        quill.insertEmbed(index, 'image', res.url, 'user');
                        quill.setSelection(index + 1, 0, 'silent');
                    } else {
                        quill.insertText(index, file.name, 'link', res.url, 'user');
                        quill.setSelection(index + file.name.length, 0, 'silent');
                    }
                }
            }
        } catch {
            // upload error — silently skip insertion
        } finally {
            setFileUploading(false);
        }
    }, []);

    const handleFileSelect = useCallback(async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            setError('File exceeds the 10 MB limit');
            setTimeout(() => setError(''), 5000);
            e.target.value = '';
            return;
        }
        await uploadAndInsert(file, 'file');
        e.target.value = '';
    }, [uploadAndInsert]);

    const handleImageSelect = useCallback(async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            setError('Image exceeds the 5 MB limit');
            setTimeout(() => setError(''), 5000);
            e.target.value = '';
            return;
        }
        await uploadAndInsert(file, 'image');
        e.target.value = '';
    }, [uploadAndInsert]);

    const quillModules = useMemo(() => ({
        toolbar: {
            container: [
                [{ header: [1, 2, 3, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ indent: '-1' }, { indent: '+1' }],
                ['link', 'image', 'file'],
                ['clean', 'hr'],
            ],
            handlers: {
                // Override default URL-prompt behaviour — upload from disk instead
                image: () => imageInputRef.current?.click(),
                file:  () => fileInputRef.current?.click(),
                hr: () => {
                    const quill = quillRef.current?.getEditor();
                    if (!quill) return;
                    const range = quill.getSelection(true);
                    const index = range ? range.index : quill.getLength();
                    quill.insertEmbed(index, 'hr', true, 'user');
                    quill.setSelection(index + 1, 0, 'silent');
                },
            },
        },
    }), []);

    const handleSubmit = async (event) => {
        event.preventDefault();

        setError('');
        setSuccess(false);
        setSubmitLoading(true);

        let message = '';

        if (!data.name || !data.slug)
            message = 'Please, enter required fields';

        if (!message) {
            const res = await (data.id ? confService.editPage(data) : confService.addPage(data));

            if (res.res === 'OK') {
                setSuccess(true);
                if (!params.id)
                    setTimeout(() => {
                        navigate(`/configuration/pages/${data.id || res.id}`)
                    }, 5000);
            } else {
                setError(res.error);
            }
        } else {
            setError(message);
        }
        setSubmitLoading(false);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        const res = await confService.editInfoPage(params.id);
        setData(res.page);
        setLoading(false);
    }, [params]);

    useEffect(() => {
        if (params.id)
            fetchData();
    }, [fetchData]);

    return (
        <>
            <h1>{params.id ? 'Edit' : 'Add'} page</h1>
            {loading ? <Loader /> : <form onSubmit={handleSubmit}>
                <div className="form-column">
                    <label>Title</label>
                    <input type="text" name="name" value={data.name || ''} onChange={(e) => changeValue(e.target.name, e.target.value)} />
                </div>
                <div className="form-column">
                    <label>Slug</label>
                    <input type="text" name="slug" value={data.slug || ''} onChange={(e) => changeValue(e.target.name, e.target.value)} />
                </div>
                <div className="form-column">
                    <label>Content {fileUploading && <Loading className="btn-loader" alt="uploading" />}</label>
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={data.content || ''}
                        onChange={(value) => changeValue('content', value)}
                        modules={quillModules}
                        formats={['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'indent', 'link', 'image', 'hr']}
                    />
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        style={{ display: 'none' }}
                        onChange={handleImageSelect}
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.docx"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                </div>
                <div className="form-submit">
                    <button type="submit" className="primary">
                        {submitLoading ? <Loading className="btn-loader" alt="loader" /> : ''}Save
                    </button>
                    {error ? <div className="error">{error}</div> : ''}
                    {success ? <div className="success">Page was successfully {data.id ? 'updated' : 'added'}</div> : ''}
                </div>
            </form>}
        </>
    );
};

export default PageEditForm;
